/**
 * Modal de confirmation pour restaurer un sondage en attente
 * Affiché après connexion si un sondage était en cours de création
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { Save, Clock, Calendar, FileText, X } from "lucide-react";
import { PendingPollData } from "../../services/PendingPollService";

interface PendingPollRestoreModalProps {
  isOpen: boolean;
  pendingPoll: PendingPollData;
  onRestore: () => Promise<void>;
  onDismiss: () => void;
  isRestoring?: boolean;
}

export function PendingPollRestoreModal({
  isOpen,
  pendingPoll,
  onRestore,
  onDismiss,
  isRestoring = false,
}: PendingPollRestoreModalProps) {
  const { t } = useTranslation("polls");
  if (!isOpen) return null;

  const { pollData, timestamp, pollType } = pendingPoll;
  const ageMinutes = Math.floor((Date.now() - timestamp) / (60 * 1000));

  // Formater l'âge du sondage
  const formatAge = (minutes: number): string => {
    if (minutes < 1) return t("pendingRestore.age.now");
    if (minutes === 1) return t("pendingRestore.age.oneMinute");
    if (minutes < 60)
      return t("pendingRestore.age.minutes", { count: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours === 1) return t("pendingRestore.age.oneHour");
    if (hours < 24) return t("pendingRestore.age.hours", { count: hours });
    const days = Math.floor(hours / 24);
    return days === 1
      ? t("pendingRestore.age.oneDay")
      : t("pendingRestore.age.days", { count: days });
  };

  // Obtenir l'icône selon le type
  const getIcon = () => {
    switch (pollType) {
      case "date":
        return <Calendar className="w-5 h-5" />;
      case "form":
        return <FileText className="w-5 h-5" />;
      default:
        return <Save className="w-5 h-5" />;
    }
  };

  // Obtenir les détails selon le type
  const getDetails = () => {
    switch (pollType) {
      case "date":
        const dateCount = pollData.selectedDates?.length || 0;
        return dateCount > 1
          ? t("pendingRestore.details.datesPlural", { count: dateCount })
          : t("pendingRestore.details.dates", { count: dateCount });
      case "form":
        const questionCount = pollData.questions?.length || 0;
        return questionCount > 1
          ? t("pendingRestore.details.questionsPlural", {
              count: questionCount,
            })
          : t("pendingRestore.details.questions", { count: questionCount });
      default:
        return t("pendingRestore.details.default");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      data-testid="pending-poll-modal"
    >
      <div className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between p-6 pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Save className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3
                className="text-lg font-semibold text-gray-900 dark:text-gray-100"
                data-testid="modal-title"
              >
                {t("pendingRestore.title")}
              </h3>
            </div>
          </div>
          <button
            onClick={onDismiss}
            disabled={isRestoring}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
            aria-label={t("pendingRestore.close")}
            data-testid="modal-close-button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 pb-6 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t("pendingRestore.prompt")}
          </p>

          {/* Poll preview card */}
          <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-start gap-3">
              <div className="text-gray-600 dark:text-gray-400 mt-0.5">
                {getIcon()}
              </div>
              <div className="flex-1 min-w-0">
                <h4
                  className="font-medium text-gray-900 dark:text-gray-100 truncate"
                  data-testid="poll-preview-title"
                >
                  {pollData.title || t("pendingRestore.untitled")}
                </h4>
                {pollData.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                    {pollData.description}
                  </p>
                )}
                <div
                  className="flex items-center gap-4 mt-2 text-sm text-gray-500 dark:text-gray-500"
                  data-testid="poll-preview-details"
                >
                  <span>{getDetails()}</span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {formatAge(ageMinutes)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={onRestore}
              disabled={isRestoring}
              className="flex-1 px-4 py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white font-medium rounded-lg hover:from-green-600 hover:to-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {isRestoring
                ? t("pendingRestore.restoring")
                : t("pendingRestore.restore")}
            </button>
            <button
              onClick={onDismiss}
              disabled={isRestoring}
              className="px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              {t("pendingRestore.dismiss")}
            </button>
          </div>

          {/* Info message */}
          <p className="text-xs text-gray-500 dark:text-gray-500 text-center">
            {t("pendingRestore.deleteWarning")}
          </p>
        </div>
      </div>
    </div>
  );
}
